import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTopOfMind,
  rebuildTopOfMind,
  topOfMindIsBusy,
  topOfMindPollInterval,
  type TopOfMindHolder,
} from '@/lib/topOfMind.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import type { TopOfMindResponse } from '@wundo/shared/topOfMind.ts';

export function topOfMindQueryKey(holder: TopOfMindHolder) {
  return ['topOfMind', holder.type, holder.id];
}

/**
 * A holder's summary, polled while it is being written, and its rebuild. The
 * rebuild counts as running for as long as the row still carries the request
 * it made: a later document change is a debounced run again.
 */
export function useTopOfMind(
  holder: TopOfMindHolder,
  onError: (message: string) => void,
) {
  const queryClient = useQueryClient();
  const queryKey = topOfMindQueryKey(holder);
  const [rebuildRequestedAt, setRebuildRequestedAt] = useState<string | null>(null);

  const rebuild = useMutation({
    mutationFn: () => rebuildTopOfMind(holder),
    onSuccess: (state) => {
      setRebuildRequestedAt(state.requested_at);
      queryClient.setQueryData(queryKey, state);
    },
    onError: async (failure) => {
      onError(apiErrorMessage(failure, 'Failed to rebuild the summary'));
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  const isOwnRebuild = (state: TopOfMindResponse | undefined): boolean =>
    rebuildRequestedAt !== null &&
    state?.requested_at === rebuildRequestedAt &&
    topOfMindIsBusy(state.status);

  const query = useQuery({
    queryKey,
    queryFn: () => getTopOfMind(holder),
    refetchInterval: (current) =>
      topOfMindPollInterval(current.state.data?.status, isOwnRebuild(current.state.data)),
  });

  return {
    state: query.data,
    queryKey,
    rebuild: () => rebuild.mutate(),
    rebuilding: rebuild.isPending || isOwnRebuild(query.data),
  };
}
